import { useContext, useReducer, createContext, useCallback } from "react";
import { useRequest } from "../../services/request";

const Context = createContext();

export function useNews() {
  return useContext(Context);
}
const DEFAULT_PAGESIZE = 150;

const initState = {
  list: [],
  filters: {
    pagesize: DEFAULT_PAGESIZE,
  },
  selected: null,
  // departmentID: null,
  // companyID: null,
  // search: "",
  count: 0,
  page: 0,
  fileName: "",
  status: "idle",
  avatar: null,
  error: null,
  statusID: 1,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "set_list":
      return { ...state, list: [...action.payload] };
    case "set_filters":
      return { ...state, filters: { ...state.filters, ...action.payload } };
    case "set_count":
      return { ...state, count: action.payload };
    case "set_fileName":
      return { ...state, fileName: action.payload };
    case "status":
      return { ...state, status: action.payload };
    case "add":
      const newList = [{ ...action.payload }, ...state.list];
      return { ...state, list: newList };
    case "add_document":
      const newDocs = [{ ...action.payload }, ...state.documents];
      return { ...state, documents: newDocs };
    case "edit":
      const modified = state.list.map((p) =>
        p.id === action.payload.id ? { ...p, ...action.payload } : p
      );
      return { ...state, list: modified };
    case "set_page":
      return { ...state, page: action.payload };
    case "set_per_page":
      return { ...state, perPage: action.payload };
    case "set_selected":
      return { ...state, selected: { ...action.payload } };
    case "delete":
      const filtered = state.list.filter((p) => p.id !== action.payload);
      return { ...state, list: filtered };
    default:
      throw new Error(`Invalid dispatch type: ${action.type}`);
  }
};

export default function NewsProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initState);
  const req = useRequest();

  const fetchList = useCallback(
    async (headersData, id) => {
      dispatch({ type: "status", payload: "fetching" });

      const defaultHeadersData = {
        ...{
          pagesize: DEFAULT_PAGESIZE,
          pagenumber: 1,
        },
        ...headersData,
      };
      if (defaultHeadersData.isActive === -1)
        delete defaultHeadersData.isActive;
      if (!defaultHeadersData.langCode || defaultHeadersData.langCode === "all")
        delete defaultHeadersData.langCode;
      const resData = await req(
        `Article/List/${id}`,
        null,
        {},
        true,
        defaultHeadersData
      );
      dispatch({ type: "status", payload: `idle` });
      dispatch({ type: "set_list", payload: resData.data.articles || [] });
      dispatch({ type: "set_count", payload: resData.data.totalSize });
    },
    [req]
  );

  const setFilters = useCallback((filters) => {
    dispatch({ type: "set_filters", payload: filters });
  }, []);

  const fetchOne = useCallback(
    (info, headersInfo) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `fetching` });
        const InfoHeadersData = {
          ...{
            langCode: headersInfo?.langCode,
          },
          ...headersInfo,
        };
        const resData = await req(
          `Article/Info/${info}`,
          null,
          {},
          true,
          InfoHeadersData
        );

        dispatch({ type: "set_selected", payload: resData.data });
        dispatch({ type: "status", payload: `idle` });
        resolve(resData.data);
      });
    },
    [req]
  );

  const create = useCallback(
    async (data) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `creating` });
        try {
          const resData = await req(
            "Article/Create",
            data,
            { method: "POST" },
            true
          );
          dispatch({ type: "add", payload: resData.data });
          // dispatch({ type: "add_document", payload: resData.data.documents });
          resolve(resData.data);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req]
  );

  const edit = useCallback(
    async (id, data) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `creating` });
        try {
          const resData = await req(
            `Article/Update/${id}`,
            data,
            { method: "PUT" },
            true
          );
          dispatch({ type: "edit", payload: resData });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req]
  );

  const remove = useCallback(
    async (id) => {
      dispatch({ type: "status", payload: `deleting ${id}` });
      await req(`Article/Delete/${id}`, {}, { method: "DELETE" }, true);
      dispatch({ type: "status", payload: "idle" });
      dispatch({ type: "delete", payload: id });
    },
    [req]
  );
  /**upload image */

  const _uploadImage = useCallback(
    (data) => {
      let formData = new FormData();
      formData.append("file", data);
      formData.append("uploadType", 1);
      return new Promise(async (resolve, reject) => {
        try {
          const resData = await req(
            "Upload/Misc",
            formData,
            { method: "POST", headers: {} },
            true
          );

          dispatch({ type: "set_fileName", payload: resData.data.fileName });
          resolve(resData.data);
        } catch (error) {
          console.log(error);
          reject(error);
        }
      });
    },
    [req]
  );
  return (
    <Context.Provider
      value={{
        state,
        fetchList,
        dispatch,
        create,
        fetchOne,
        edit,
        remove,
        setFilters,
        _uploadImage,
      }}
    >
      {children}
    </Context.Provider>
  );
}
